// components/models/Flux3ImageControlPanel.tsx
import React, { useCallback, memo } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import { Flux3ImageParams, Flux3ImageResolution, Flux3KleinParams, Flux3KleinResolution } from '#types/types';
import { FLUX_3_IMAGE_DEFAULT_RESOLUTION } from '#lib/flux-3-image';
import { FLUX_3_KLEIN_DEFAULT_RESOLUTION } from '#lib/flux-3-klein';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

export const getDefaultFlux3ImageParams = (): Flux3ImageParams => ({
  prompt: '',
  seed: -1,
  resolution: FLUX_3_IMAGE_DEFAULT_RESOLUTION,
});

export const getDefaultFlux3KleinParams = (): Flux3KleinParams => ({
  prompt: '',
  seed: -1,
  resolution: FLUX_3_KLEIN_DEFAULT_RESOLUTION,
});

const FLUX_3_IMAGE_RESOLUTIONS: Flux3ImageResolution[] = ['512sq', '768sq', '1k', '2k', '4k'];
export const FLUX_3_KLEIN_RESOLUTIONS: Flux3KleinResolution[] = ['1k', '2k', '4k'];
const SELECT_STYLE = { width: '100%' };

/** Flux 3 Image and Flux 3 Klein share this panel; only their resolution tiers differ. */
const Flux3ImageControlPanel: React.FC<{ resolutions?: readonly string[] }> = ({ resolutions = FLUX_3_IMAGE_RESOLUTIONS }) => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as Flux3ImageParams | Flux3KleinParams | undefined
  );

  const updateParam = useCallback((name: string, value: string) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return { ...prevModelParams, [diffusionModel]: { ...currentParams, [name]: value } };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value_length: value.length,
      model_name: diffusionModel,
      source_component: 'Flux3ImageControlPanel',
    });
  }, [kernel]);

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateParam(e.target.name, e.target.value);
  }, [updateParam]);

  const handleResolutionChange = useCallback((val: string) => {
    updateParam('resolution', val);
  }, [updateParam]);

  if (!params) return null;

  return (
    <>
      <div>
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the image, or what to change..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mt-4">
        <Select
          id="resolution"
          label="Resolution"
          value={params.resolution}
          options={resolutions as string[]}
          onChange={handleResolutionChange}
          style={SELECT_STYLE}
        />
      </div>
    </>
  );
};

export default memo(Flux3ImageControlPanel);
